<!--
 * @Description: 代缴税金列表
 * @Date: 2026-03-25 15:35:34
-->

<template>
  <div>
    <XrkTable
      ref="xrkTableRef"
      :getListAjax="getListAjax"
      :searchConfig="searchConfig"
      :tableConfig="tableConfig"
    />
  </div>
</template>
<script lang="tsx" setup>
import { ref, computed } from 'vue';
import { WarningFilled } from '@element-plus/icons-vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useSelectColumn,
  useDatePickerColumn,
  useCreateModal,
  BaseButtonMore,
  useInputColumn,
  BaseButton,
  XrkMessage,
  XrkMessageBox,
  BaseIcon,
  BasePopover
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  listProxyTax,
  listPark,
  downloadBatchFile,
  proxyTaxListExport,
  deleteProxyTax
} from '../../data-source/apis';
import { PROXY_TAX_AUDIT_STATUS, DECLARE_METHOD } from '../../constants';
import InvoiceDjsjAddDialog from './invoice-djsj-add-dialog.vue';
import InvoiceDjsjAuditDialog from './invoice-djsj-audit-dialog.vue';

const xrkTableRef = ref();

const selectionRows = computed(() => {
  return xrkTableRef.value.getSelectionRows() || [];
});
/**
 * 获取列表数据
 * @description: 调用代缴税金列表接口
 */
const getListAjax = async params => {
  return listProxyTax(params);
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    useInputColumn({
      prop: 'individualName',
      label: '个体工商户名称',
      config: { clearable: true }
    }),
    useInputColumn({
      prop: 'entpCertNo',
      label: '统一社会信用代码',
      config: { clearable: true }
    }),
    useInputColumn({
      prop: 'operator',
      label: '经营者',
      config: { clearable: true }
    }),
    useInputColumn({
      prop: 'phone',
      label: '手机号',
      config: { clearable: true }
    }),
    useDatePickerColumn({
      prop: 'incomeMonth',
      label: '所属月份',
      config: {
        type: 'monthrange',
        startPlaceholder: '开始月份',
        endPlaceholder: '结束月份',
        clearable: true
      },
      listener: {
        onChange: (value, searchObj) => {
          if (!value) {
            searchObj.incomeMonthStart = '';
            searchObj.incomeMonthEnd = '';
          } else {
            searchObj.incomeMonthStart = value[0];
            searchObj.incomeMonthEnd = value[1];
          }
        }
      }
    }),

    useSelectColumn({
      prop: 'businessParkId',
      label: '所属园区',
      config: {
        filterUseRemote: true,
        remoteMethod: async (val: string) => {
          return await listPark({
            name: val,
            enable: 1
          });
        },
        labelKey: 'businessParkName',
        valueKey: 'id'
      }
    }),
    useSelectColumn({
      prop: 'declareMethod',
      label: '申报方式',
      config: {
        clearable: true,
        placeholder: '请选择',
        options: Object.values(DECLARE_METHOD)
      }
    }),
    useSelectColumn({
      prop: 'auditStatus',
      label: '状态',
      config: {
        clearable: true,
        placeholder: '请选择',
        options: Object.values(PROXY_TAX_AUDIT_STATUS)
      }
    })
  ],
  searchBtn: { show: true, text: '搜索' },
  resetBtn: { show: true, text: '重置' },
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              type="primary2"
              click={async () => {
                if (!selectionRows.value.length) {
                  XrkMessage.warning('请选择需要导出的数据');
                  return;
                }
                await downloadBatchFile({
                  proxyTaxIdList: selectionRows.value.map(item => item.id)
                });
                XrkMessage.success('操作成功，请前往下载中心进行下载查看');
              }}
            >
              导出材料
            </BaseButton>
            <BaseButton
              type="primary2"
              click={async () => {
                const tableData = xrkTableRef.value.getTableData();
                if (!tableData.value.length) {
                  XrkMessage.warning('没有符合当前搜索条件的数据可以导出');
                  return;
                }
                await proxyTaxListExport({
                  proxyTaxIdList: tableData.value.map(item => item.id)
                });
              }}
            >
              导出列表字段
            </BaseButton>
            <BaseButton
              type="primary2"
              click={async () => {
                useCreateModal(InvoiceDjsjAddDialog, {
                  onSuccess() {
                    xrkTableRef.value.initCurrentList();
                  }
                });
              }}
            >
              新增
            </BaseButton>
          </>
        );
      }
    }
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  baseConfig: { rowKey: 'id' },
  tableColumns: [
    useTableColumn({
      type: '_selection',
      prop: 'id',
      align: 'center',
      fixed: 'left',
      width: 120,
      selectionDisabled(row) {
        return ![2].includes(row.auditStatus);
      }
    }),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    useTableColumn({
      label: '个体工商户名称',
      prop: 'individualName',
      width: 200
    }),
    useTableColumn({
      label: '统一社会信用代码',
      prop: 'entpCertNo',
      width: 180
    }),
    useTableColumn({
      label: '经营者',
      prop: 'operator',
      width: 120
    }),
    useTableColumn({
      label: '手机号',
      prop: 'phone',
      width: 130
    }),
    useTableColumn({
      label: '所属园区',
      prop: 'businessParkName',
      width: 150
    }),
    useTableColumn({
      label: '申报方式',
      prop: 'declareMethod',
      width: 100,
      formatter: ({ cellValue }) => DECLARE_METHOD[cellValue]?.label || '-'
    }),
    useTableColumn({
      label: '所属月份',
      prop: 'incomeMonthStart',
      width: 180,
      formatter: ({ row }) => {
        const start = row.incomeMonthStart || '';
        const end = row.incomeMonthEnd || '';
        return start && end ? `${start} ~ ${end}` : start || end || '-';
      }
    }),
    useTableColumn({
      label: '代缴税金(元)',
      prop: 'proxyTaxAmount',
      width: '140px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '创建时间',
      prop: 'createAt',
      width: '180px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '更新时间',
      prop: 'updateAt',
      width: '180px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '状态',
      prop: 'auditStatus',
      width: '120px',
      render: ({ row, cellValue }) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span>
              {formatter.defaultFormat(
                cellValue,
                () => PROXY_TAX_AUDIT_STATUS[cellValue]?.label || '-'
              )}
            </span>
            {[3].includes(cellValue) && row.auditReason && (
              <BasePopover
                placement="top"
                width="300px"
                title="驳回原因"
                trigger="hover"
                content={row.auditReason}
                disabled={false}
                v-slots={{
                  reference: () => (
                    <BaseIcon>
                      <WarningFilled />
                    </BaseIcon>
                  )
                }}
              />
            )}
          </div>
        );
      }
    }),
    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      width: '120px',
      render: ({ row }) => {
        const btns = [
          !row.auditStatus && {
            content: '上传',
            click: () => {
              useCreateModal(InvoiceDjsjAuditDialog, {
                id: row.id,
                onSuccess() {
                  xrkTableRef.value.initCurrentList();
                }
              });
            }
          },
          !row.auditStatus && {
            content: '删除',
            click: async () => {
              await XrkMessageBox.confirm(
                '确定要删除该代缴税金吗?',
                '删除确认',
                {
                  confirmButtonText: '确定',
                  cancelButtonText: '取消',
                  type: 'warning'
                }
              );
              await deleteProxyTax({ id: row.id });
              XrkMessage.success('删除成功');
              xrkTableRef.value.initCurrentList();
            }
          },
          row.auditStatus == 2 && {
            content: '下载',
            click: async () => {
              await downloadBatchFile({
                proxyTaxIdList: [row.id]
              });
              XrkMessage.success('操作成功，请前往下载中心进行下载查看');
            }
          }
        ].filter(Boolean);
        return (
          <BaseButtonMore
            key={Date.now()}
            maxCount={2}
            style={{ 'justify-content': 'right' }}
            btns={btns}
          ></BaseButtonMore>
        );
      }
    })
  ].filter(item => item)
});
</script>
